<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="storeaccesses"
      hide-actions
      :class="{'less-padding': $vuetify.breakpoint.xs}"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <td
          style="word-break: break-word; width: 400px"
          :title="props.item.store.city + ', ' + props.item.store.address"
        >
          <v-layout align-center>
            <v-icon
              color="msr"
              class="mr-2"
            >
              location_on
            </v-icon>
            <v-layout column>
              <small class="grey--text text--darken-2">{{
                props.item.store.name
              }}</small>
              <div>
                <span class="body-2">{{ props.item.store.city }}</span>
                <span class="grey--text">{{ props.item.store.location }}</span>
              </div>
            </v-layout>
          </v-layout>
        </td>
        <td>
          <v-layout>
            <!-- Level: Read -->
            <v-btn
              icon
              dark
              flat
              class="ml-0"
              :title="$t('userManagementStoreAccessRead')"
              :color="colors.active"
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              data-cy="toggle-storeaccess-read"
              @click="
                isAllowedToModify ? onStoreAccessClickedRead(props.item) : null
              "
            >
              <v-icon medium>
                visibility
              </v-icon>
            </v-btn>
            <!-- Level: Write -->
            <v-btn
              icon
              flat
              dark
              :title="$t('userManagementStoreAccessWrite')"
              :class="{'ml-0': $vuetify.breakpoint.xs}"
              :color="
                props.item.level === GA_ACCESS_LEVEL_FULL
                  ? colors.active
                  : colors.inactive
              "
              :disabled="
                !storeIdToAllowances[props.item.store.id] ||
                  !storeIdToAllowances[props.item.store.id]['write']
              "
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              data-cy="toggle-storeaccess-write"
              @click="
                isAllowedToModify ? onStoreAccessClickedWrite(props.item) : null
              "
            >
              <v-icon medium>
                edit
              </v-icon>
            </v-btn>
            <!-- Visitorcount Permission -->
            <v-btn
              icon
              flat
              dark
              :title="$t('visitorcount')"
              :class="{'ml-0': $vuetify.breakpoint.xs}"
              :color="
                props.item.can_access_visitorcount
                  ? colors.active
                  : colors.inactive
              "
              :disabled="
                !storeIdToAllowances[props.item.store.id] ||
                  !storeIdToAllowances[props.item.store.id]['visitorcount']
              "
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              data-cy="toggle-storeaccess-visitorcount"
              @click="
                isAllowedToModify
                  ? onStoreAccessClickedVisitorcount(props.item)
                  : null
              "
            >
              <v-icon medium>
                timeline
              </v-icon>
            </v-btn>
          </v-layout>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import {deepCopyObject} from '@/utils';

import accessConstants from '@/mixins/accessConstants.js';

export default {
  name: 'StoreAccessPermissionsTable',
  mixins: [accessConstants],
  props: {
    user: {
      type: Object,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    /** The StoreAccess objects the User's creator has. */
    availableStoreaccesses: {
      type: Array[Object],
      required: true,
    },
    /** The StoreAccess objects the User currently has. */
    actualStoreaccesses: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      headers: [
        {text: this.$t('store'), value: 'store.city', sortable: true},
        {text: this.$t('permission'), sortable: false},
      ],
      colors: {
        inactive: 'grey lighten-1',
        active: 'msr',
      },
    };
  },
  computed: {
    layout() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return {column: true};
      }
      return {row: true};
    },
    updating: {
      get() {
        return this.busy;
      },
      set(value) {
        this.$emit('update:busy', value);
      },
    },
    /**
     * The copying in here avoids vuex barking at us when we try to
     * modify the objects (e.g. to remove the write-permission).
     */
    storeaccesses() {
      return this.actualStoreaccesses.map((actualStoreaccess) =>
        deepCopyObject(actualStoreaccess)
      );
    },
    /**
     * Lookup to check whether a permission can be given based on creator's access.
     */
    storeIdToAllowances() {
      const storeIdToAllowances = {};
      for (let storeaccess of this.availableStoreaccesses) {
        storeIdToAllowances[storeaccess.store.id] = {
          read: true,
          write: storeaccess.level === this.GA_ACCESS_LEVEL_FULL,
          visitorcount: storeaccess.can_access_visitorcount,
        };
      }
      return storeIdToAllowances;
    },
    /**
     * Only a Group Manager/Coadmin may modify the managed User (but not himself).
     */
    isAllowedToModify() {
      return (
        this.$store.getters.isUsermanagerOrManagedCoadmin &&
        this.$user.id !== this.user.id
      );
    },
  },
  methods: {
    onStoreAccessClickedRead(storeaccess) {
      const hasAccessAlready = !!storeaccess.id && storeaccess.level !== null;
      if (hasAccessAlready) {
        // Mark for deletion.
        storeaccess.level = null;
      } else {
        storeaccess.level = this.GA_ACCESS_LEVEL_READ;
      }
      this.onStoreAccessChanged(storeaccess);
    },
    onStoreAccessClickedWrite(storeaccess) {
      if (storeaccess.level === this.GA_ACCESS_LEVEL_READ) {
        storeaccess.level = this.GA_ACCESS_LEVEL_FULL;
      } else {
        storeaccess.level = this.GA_ACCESS_LEVEL_READ;
      }
      this.onStoreAccessChanged(storeaccess);
    },
    onStoreAccessClickedVisitorcount(storeaccess) {
      storeaccess.can_access_visitorcount =
        !storeaccess.can_access_visitorcount;
      this.onStoreAccessChanged(storeaccess);
    },
    /** An existing StoreAccess has been changed. */
    onStoreAccessChanged(storeaccess) {
      const canBeDeleted = storeaccess.level === null;
      if (canBeDeleted) {
        this.deleteManagedUserStoreAccess(this.user, storeaccess.id);
      } else {
        this.updateManagedUserStoreAccess(this.user, storeaccess);
      }
    },
    updateManagedUserStoreAccess(user, storeaccess) {
      this.updating = true;
      return this.$api
        .updateManagedUserStoreAccess(user.id, storeaccess.id, storeaccess)
        .then((response) => {
          // Do nothing.
          const storeaccess = response.data;
          this.$store.commit('UPDATE_STOREACCESS_OF_MANAGED_USER', {
            user: user,
            storeaccess: storeaccess,
          });
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.updating = false;
        });
    },
    deleteManagedUserStoreAccess(user, storeaccessId) {
      this.updating = true;
      return this.$api
        .deleteManagedUserStoreAccess(user.id, storeaccessId)
        .then(() => {
          this.$store.commit('REMOVE_STOREACCESS_FROM_MANAGED_USER', {
            user: user,
            storeaccessId: storeaccessId,
          });
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.updating = false;
        });
    },
  },
};
</script>

<style scoped>
.less-padding th,
.less-padding td {
  padding: 16px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/UserManagement/StoreAccessPermissionsTable.vue